import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Save,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import StatusBadge from "./pricerequests/StatusBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  "pending",
  "researching",
  "quoted",
  "closed",
  "requires_attention",
];

const formatCurrency = (amount, currency = "USD") => {
  if (amount === null || amount === undefined) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `$${Number(amount).toFixed(2)}`;
  }
};

const formatDate = (raw) => {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return raw;
  }
};

/**
 * Small admin console for reviewing every quotation request stored in Mongo:
 * change status, override per-line unit prices, paste in a PDF URL.
 * Renders inside the authenticated AdminPanel below the existing product manager.
 */
const AdminQuotationReview = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [requestId]: { status, pdf_url, products: {[idx]: unit_price} } }
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/price_requests`);
      const list = Array.isArray(data?.requests) ? data.requests : [];
      setItems(list);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Could not load quotation requests."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const draftFor = (item) => {
    const d = drafts[item.id] || {};
    return {
      status: d.status ?? item.status ?? "pending",
      pdf_url: d.pdf_url ?? item.pdf_url ?? "",
      products: d.products || {},
    };
  };

  const setDraftField = (id, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...patch },
    }));
  };

  const setProductPriceDraft = (id, index, value) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        products: { ...((prev[id] || {}).products || {}), [index]: value },
      },
    }));
  };

  const handleSave = async (item) => {
    const draft = draftFor(item);
    setSavingId(item.id);
    setError("");
    setSuccess("");
    try {
      const productsPayload = Object.entries(draft.products)
        .map(([idx, val]) => {
          const num = parseFloat(val);
          if (Number.isNaN(num) || num < 0) return null;
          return { index: Number(idx), unit_price: num };
        })
        .filter(Boolean);
      const body = {
        status: draft.status,
        pdf_url: draft.pdf_url || "",
      };
      if (productsPayload.length > 0) body.products = productsPayload;
      const { data } = await axios.patch(
        `${API}/admin/price_requests/${item.id}`,
        body
      );
      // Replace the item with the fresh serialised version.
      setItems((prev) => prev.map((it) => (it.id === item.id ? data : it)));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      setSuccess(`Request ${data.request_number} updated.`);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Could not save the changes."
      );
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Card className="mb-8" data-testid="admin-quotation-review">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Quotation Requests</CardTitle>
          <p className="text-sm text-gray-500">
            Review requests, override AI prices, change status and attach a
            quotation PDF URL.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="admin-quote-refresh"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert className="mb-4 bg-red-50 border-red-200">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription
              className="text-red-800"
              data-testid="admin-quote-error"
            >
              {error}
            </AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-4 bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription
              className="text-green-800"
              data-testid="admin-quote-success"
            >
              {success}
            </AlertDescription>
          </Alert>
        )}

        {loading && items.length === 0 ? (
          <div className="text-center py-8 text-gray-600">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading requests...
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No quotation requests yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-quote-table">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b">
                  <th className="py-2 pr-4">Request #</th>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Products</th>
                  <th className="py-2 pr-4 text-right">Est. Total</th>
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const expanded = expandedId === item.id;
                  const draft = draftFor(item);
                  const productsList = item.products || [];
                  return (
                    <React.Fragment key={item.id}>
                      <tr className="border-b border-gray-100 hover:bg-gray-50 align-top">
                        <td className="py-3 pr-4 font-semibold text-[#1E3A5F]">
                          {item.request_number || "—"}
                        </td>
                        <td className="py-3 pr-4">{item.username || "—"}</td>
                        <td className="py-3 pr-4">
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="py-3 pr-4 text-gray-600">
                          {item.products_count || productsList.length}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold">
                          {formatCurrency(
                            item.total_estimated_price,
                            item.currency
                          )}
                        </td>
                        <td className="py-3 pr-4 text-gray-600">
                          {formatDate(item.created_at)}
                        </td>
                        <td className="py-3 pr-4">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedId(expanded ? null : item.id)
                            }
                            className="text-cyan-700 hover:text-cyan-900 font-semibold inline-flex items-center gap-1"
                            data-testid={`admin-quote-toggle-${item.id}`}
                          >
                            {expanded ? (
                              <>
                                <ChevronUp className="w-4 h-4" /> Close
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4" /> Review
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="bg-slate-50">
                          <td colSpan={7} className="p-4">
                            <div className="grid md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                  Status
                                </label>
                                <select
                                  value={draft.status}
                                  onChange={(e) =>
                                    setDraftField(item.id, {
                                      status: e.target.value,
                                    })
                                  }
                                  data-testid={`admin-quote-status-${item.id}`}
                                  className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm w-full"
                                >
                                  {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                      {s.replace(/_/g, " ")}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                  Quotation PDF URL (optional)
                                </label>
                                <Input
                                  type="url"
                                  placeholder="https://…"
                                  value={draft.pdf_url}
                                  onChange={(e) =>
                                    setDraftField(item.id, {
                                      pdf_url: e.target.value,
                                    })
                                  }
                                  data-testid={`admin-quote-pdf-${item.id}`}
                                />
                              </div>
                            </div>

                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left uppercase text-gray-500 border-b">
                                    <th className="py-1 pr-3">Product</th>
                                    <th className="py-1 pr-3">Qty</th>
                                    <th className="py-1 pr-3">Source</th>
                                    <th className="py-1 pr-3">Conf.</th>
                                    <th className="py-1 pr-3 text-right">
                                      Unit price
                                    </th>
                                    <th className="py-1 pr-3 text-right">
                                      Line total
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {productsList.map((p, idx) => {
                                    const current =
                                      draft.products[idx] !== undefined
                                        ? draft.products[idx]
                                        : p.unit_price ?? "";
                                    return (
                                      <tr
                                        key={idx}
                                        className="border-b last:border-0 align-top"
                                      >
                                        <td className="py-2 pr-3 max-w-[280px]">
                                          <div className="font-semibold text-[#1E3A5F]">
                                            {p.description || "—"}
                                          </div>
                                          <div className="text-gray-500">
                                            {[p.brand, p.model, p.manufacturer]
                                              .filter(Boolean)
                                              .join(" · ") || ""}
                                          </div>
                                          {p.research_source_url && (
                                            <a
                                              href={p.research_source_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 underline"
                                              data-testid="admin-quote-source-link"
                                            >
                                              View source
                                            </a>
                                          )}
                                        </td>
                                        <td className="py-2 pr-3">
                                          {p.quantity ?? 1}
                                        </td>
                                        <td className="py-2 pr-3 capitalize">
                                          {p.research_source || "—"}
                                        </td>
                                        <td className="py-2 pr-3">
                                          {p.research_confidence != null
                                            ? `${p.research_confidence}%`
                                            : "—"}
                                        </td>
                                        <td className="py-2 pr-3 text-right">
                                          <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={current}
                                            onChange={(e) =>
                                              setProductPriceDraft(
                                                item.id,
                                                idx,
                                                e.target.value
                                              )
                                            }
                                            data-testid={`admin-quote-unit-${item.id}-${idx}`}
                                            className="h-8 text-right w-24 ml-auto"
                                          />
                                        </td>
                                        <td className="py-2 pr-3 text-right font-semibold">
                                          {p.line_total != null
                                            ? formatCurrency(
                                                p.line_total,
                                                item.currency
                                              )
                                            : "—"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            <div className="flex justify-end mt-4">
                              <Button
                                onClick={() => handleSave(item)}
                                disabled={savingId === item.id}
                                data-testid={`admin-quote-save-${item.id}`}
                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full"
                              >
                                {savingId === item.id ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Save changes
                                  </>
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminQuotationReview;
